import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCan } from '@/app/hooks';
import { useListEmployeesQuery } from '@/features/employees/employeesApi';

interface Result {
  id: string;
  label: string;
  hint: string;
  to: string;
}

const PAGES: { label: string; to: string; capability?: string }[] = [
  { label: 'Leave', to: '/leave' },
  { label: 'Absence', to: '/sickness' },
  { label: 'Documents', to: '/documents' },
  { label: 'Benefits', to: '/benefits' },
  { label: 'Training', to: '/training' },
  { label: 'Handbook', to: '/handbook' },
  { label: 'Policies', to: '/policies' },
  { label: 'Contacts directory', to: '/directory' },
  { label: 'Calendar', to: '/calendar' },
  { label: 'Messages', to: '/messages' },
  { label: 'Notifications', to: '/notifications' },
  { label: 'Your profile', to: '/profile' },
  { label: 'Help', to: '/help' },
  { label: 'People', to: '/people', capability: 'employee:read:reports' },
  { label: 'Approvals', to: '/approvals', capability: 'employee:read:reports' },
  { label: 'Absence and cost', to: '/analytics', capability: 'employee:read:reports' },
  { label: 'Payroll documents', to: '/admin/payroll', capability: 'employee:write:all' },
  { label: 'Controlled documents', to: '/admin/documents', capability: 'employee:write:all' },
  { label: 'Policy administration', to: '/admin/policies', capability: 'employee:write:all' },
  { label: 'Contracts', to: '/admin/contracts', capability: 'employee:write:all' },
  { label: 'Benefits administration', to: '/admin/benefits', capability: 'employee:write:all' },
  { label: 'Training administration', to: '/admin/training', capability: 'employee:write:all' },
  { label: 'Departments', to: '/admin/organisation', capability: 'employee:write:all' },
  { label: 'Add an employee', to: '/admin/people', capability: 'employee:create' },
  { label: 'Data and audit', to: '/admin/data', capability: 'employee:write:all' },
  { label: 'Accounts', to: '/admin/accounts', capability: 'user:manage' },
  { label: 'Settings', to: '/admin/settings', capability: 'user:manage' },
];

/**
 * Search across people and screens.
 *
 * People come from `/employees?search=`, which `scopeFilter` already scopes —
 * so this cannot surface someone the caller could not otherwise open. It holds
 * no client-side copy of the directory; each keystroke asks the server, which
 * is the only way the result set stays inside the caller's permissions.
 */
export function Omnibox() {
  const navigate = useNavigate();
  const can = useCan();
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term.trim()), 220);
    return () => clearTimeout(timer);
  }, [term]);

  const { data: people } = useListEmployeesQuery(
    debounced ? { search: debounced } : undefined,
    { skip: !open || debounced.length < 2 },
  );

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const typingElsewhere =
        event.target instanceof HTMLElement &&
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target.tagName);

      if ((event.key === 'k' && (event.metaKey || event.ctrlKey)) || (event.key === '/' && !typingElsewhere)) {
        event.preventDefault();
        setOpen(true);
        setTimeout(() => inputRef.current?.focus(), 0);
      }
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const results = useMemo<Result[]>(() => {
    const needle = debounced.toLowerCase();
    if (!needle) return [];

    const pageHits = PAGES.filter(
      (page) =>
        page.label.toLowerCase().includes(needle) &&
        (!page.capability || can(page.capability as never)),
    ).map((page) => ({ id: `page:${page.to}`, label: page.label, hint: 'Screen', to: page.to }));

    const peopleHits = (people?.employees ?? []).slice(0, 8).map((employee) => ({
      id: `person:${employee.id}`,
      label: employee.fullName,
      hint: [employee.jobTitle, employee.departmentName].filter(Boolean).join(' · ') || 'Employee',
      to: `/people/${employee.id}`,
    }));

    return [...peopleHits, ...pageHits].slice(0, 12);
  }, [debounced, people, can]);

  useEffect(() => setActive(0), [results.length]);

  const go = (result: Result | undefined) => {
    if (!result) return;
    setOpen(false);
    setTerm('');
    navigate(result.to);
  };

  if (!open) {
    return (
      <button
        className="omnibox-trigger"
        onClick={() => {
          setOpen(true);
          setTimeout(() => inputRef.current?.focus(), 0);
        }}
        aria-label="Search people and screens"
      >
        Search <kbd>⌘K</kbd>
      </button>
    );
  }

  return (
    <div className="modal-backdrop" onClick={() => setOpen(false)}>
      <div className="omnibox" onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef}
          className="omnibox__input"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Search people and screens…"
          role="combobox"
          aria-expanded
          aria-controls="omnibox-results"
          aria-activedescendant={results[active] ? `omnibox-${results[active].id}` : undefined}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setActive((i) => Math.min(i + 1, results.length - 1));
            }
            if (event.key === 'ArrowUp') {
              event.preventDefault();
              setActive((i) => Math.max(i - 1, 0));
            }
            if (event.key === 'Enter') {
              event.preventDefault();
              go(results[active]);
            }
          }}
        />

        <ul className="omnibox__results" id="omnibox-results" role="listbox">
          {results.map((result, index) => (
            <li
              key={result.id}
              id={`omnibox-${result.id}`}
              role="option"
              aria-selected={index === active}
              className={index === active ? 'is-active' : ''}
              onMouseEnter={() => setActive(index)}
              onClick={() => go(result)}
            >
              <span>{result.label}</span>
              <small className="muted">{result.hint}</small>
            </li>
          ))}
          {debounced.length >= 2 && results.length === 0 ? (
            <li className="muted">Nothing matches.</li>
          ) : null}
          {debounced.length < 2 ? <li className="muted">Type at least two characters.</li> : null}
        </ul>
      </div>
    </div>
  );
}
